import { useState, useEffect, useCallback, useMemo } from 'react';
import { ShareLinkControl } from '@/cowork/dashboards/ShareLinkControl';
import { DashboardStudio } from '@/cowork/dashboards/DashboardStudio';
import { DashboardPreview } from '@/cowork/dashboards/DashboardPreview';
import { CreateDashboardDialog } from '@/cowork/dashboards/CreateDashboardDialog';
import {
  ArrowLeft,
  BarChart3,
  RefreshCw,
  Trash2,
  Loader2,
  Clock,
  Download,
  CheckSquare,
  Square,
  MoreHorizontal,
  Pencil,
  Copy,
  Sparkles,
  Zap,
  AlertTriangle,
  ExternalLink, Plus, Lock, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { coworkApi } from '../api/coworkApi';
import type { Dashboard, DashboardDelivery } from '../types/cowork';
import { PresentationModal } from './PresentationModal';

// ── Type badge colors ─────────────────────────────────────────────
const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  okr: { bg: '#eef2ff', text: '#6366f1', label: 'OKR' },
  analytics: { bg: '#ecfdf5', text: '#059669', label: 'Analytics' },
  content: { bg: '#fef3c7', text: '#d97706', label: 'Content' },
  campaign: { bg: '#fce7f3', text: '#db2777', label: 'Campaign' },
  custom: { bg: '#f3f4f6', text: '#6b7280', label: 'Custom' },
};

const INTERVAL_OPTIONS: Array<{ hours: number; label: string }> = [
  { hours: 1, label: 'Every hour' },
  { hours: 3, label: 'Every 3 hours' },
  { hours: 6, label: 'Every 6 hours' },
  { hours: 12, label: 'Every 12 hours' },
  { hours: 24, label: 'Daily' },
  { hours: 48, label: 'Every 2 days' },
  { hours: 168, label: 'Weekly' },
];

const POLL_MS = 8000;

function intervalLabel(hours: number): string {
  const opt = INTERVAL_OPTIONS.find(o => o.hours === hours);
  if (opt) return opt.label.replace('Every ', 'every ').replace('Daily', 'daily').replace('Weekly', 'weekly');
  return `every ${hours}h`;
}

function formatTime(iso?: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString();
}

const btnSecondary =
  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#d0d5dd] text-[12px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50 transition-colors';
const btnPrimary =
  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#be43ff] text-white text-[12px] font-semibold hover:bg-[#a835e0] disabled:opacity-50 transition-colors';

// ── Props ─────────────────────────────────────────────────────────
interface DashboardsPageProps {
  brandId: string;
  agents: Array<{ id: string; name: string; avatarEmoji: string; accentColor: string }>;
  onBack: () => void;
}

// ── Status chip ───────────────────────────────────────────────────
function RenderStatusChip({ dash, size = 'sm' }: { dash: Dashboard; size?: 'sm' | 'md' }) {
  const cls = size === 'md' ? 'text-[11px] px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  if (dash.renderStatus === 'rendering') {
    return (
      <span className={cn('inline-flex items-center gap-1 rounded-full bg-[#eef2ff] text-[#6366f1] font-medium', cls)}>
        <Loader2 className="w-3 h-3 animate-spin" /> Rendering…
      </span>
    );
  }
  if (dash.renderStatus === 'failed') {
    return (
      <span title={dash.renderError || ''} className={cn('inline-flex items-center gap-1 rounded-full bg-[#fef3f2] text-[#b42318] font-medium', cls)}>
        <AlertTriangle className="w-3 h-3" /> Failed
      </span>
    );
  }
  return null;
}

function AlwaysOnChip({ dash, size = 'sm' }: { dash: Dashboard; size?: 'sm' | 'md' }) {
  if (!dash.autoRefresh) return null;
  const cls = size === 'md' ? 'text-[11px] px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  return (
    <span
      title={dash.templateReady ? `Data re-runs ${intervalLabel(dash.refreshIntervalHours)} (no AI re-render)` : 'Needs one Redesign before scheduled refreshes can run'}
      className={cn('inline-flex items-center gap-1 rounded-full font-medium', cls, dash.templateReady ? 'bg-[#ecfdf5] text-[#047857]' : 'bg-[#fffaeb] text-[#b54708]')}
    >
      <Zap className="w-3 h-3" /> Always-on · {intervalLabel(dash.refreshIntervalHours)}
    </span>
  );
}

// ── Always-on settings dialog ─────────────────────────────────────
function AlwaysOnDialog({
  dash, onClose, onSaved,
}: { dash: Dashboard; onClose: () => void; onSaved: (d: Dashboard) => void }) {
  const [enabled, setEnabled] = useState(dash.autoRefresh);
  const [hours, setHours] = useState(dash.refreshIntervalHours || 24);
  const [inApp, setInApp] = useState(dash.delivery?.inApp !== false);
  const [slackChannel, setSlackChannel] = useState(dash.delivery?.slackChannel || '');
  const [emails, setEmails] = useState((dash.delivery?.emails || []).join(', '));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const delivery: DashboardDelivery = {
        inApp,
        slackChannel: slackChannel.trim() || undefined,
        emails: emails.split(/[,\s]+/).map(e => e.trim()).filter(e => e.includes('@')),
      };
      const updated = await coworkApi.dashboards.update(dash.id, {
        autoRefresh: enabled,
        refreshIntervalHours: hours,
        delivery,
      });
      onSaved(updated);
      toast.success(enabled ? `Always-on: data re-runs ${intervalLabel(hours)}` : 'Always-on turned off');
      onClose();
    } catch (err) {
      console.error('[DashboardsPage] Save always-on failed:', err);
      toast.error('Could not save always-on settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]"><Zap className="w-4 h-4 text-[#047857]" /> Always-on dashboard</DialogTitle>
          <DialogDescription className="text-[12px]">
            Re-runs the data sources on a schedule and re-injects them into the saved layout. No AI re-render, so it costs nothing to keep current.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <label className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-medium text-[#101828]">Refresh automatically</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </label>

          <div className={cn('space-y-4', !enabled && 'opacity-50 pointer-events-none')}>
            <div>
              <div className="text-[12px] font-medium text-[#344054] mb-1">Interval</div>
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="w-full text-[13px] border border-[#d0d5dd] rounded-lg px-2.5 py-1.5 bg-white"
              >
                {INTERVAL_OPTIONS.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <div className="text-[12px] font-medium text-[#344054] mb-1">After each refresh</div>
              <label className="flex items-center gap-2 text-[13px] text-[#344054] mb-2">
                <input type="checkbox" checked={inApp} onChange={(e) => setInApp(e.target.checked)} className="accent-[#be43ff]" />
                In-app notification
              </label>
              <input
                value={slackChannel}
                onChange={(e) => setSlackChannel(e.target.value)}
                placeholder="Slack channel, e.g. #merrell-reports (optional)"
                className="w-full text-[13px] border border-[#d0d5dd] rounded-lg px-2.5 py-1.5 mb-2"
              />
              <input
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Email recipients, comma-separated (optional)"
                className="w-full text-[13px] border border-[#d0d5dd] rounded-lg px-2.5 py-1.5"
              />
            </div>

            {!dash.templateReady && (
              <div className="flex items-start gap-2 text-[12px] text-[#b54708] bg-[#fffaeb] border border-[#fedf89] rounded-lg px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                This dashboard was rendered before saved layouts existed. Run <b>Redesign</b> once so scheduled refreshes can reuse its layout.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className={btnSecondary}>Cancel</button>
          <button onClick={save} disabled={saving} className={btnPrimary}>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit details dialog ───────────────────────────────────────────
function EditDialog({ dash, onClose, onSaved }: { dash: Dashboard; onClose: () => void; onSaved: (d: Dashboard) => void }) {
  const [title, setTitle] = useState(dash.title);
  const [description, setDescription] = useState(dash.description || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const updated = await coworkApi.dashboards.update(dash.id, { title: title.trim(), description });
      onSaved(updated);
      onClose();
    } catch (err) {
      console.error('[DashboardsPage] Edit failed:', err);
      toast.error('Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Edit dashboard</DialogTitle>
          <DialogDescription className="text-[12px]">The title and description shown in the gallery. The rendered page updates on the next refresh.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full text-[13px] border border-[#d0d5dd] rounded-lg px-2.5 py-1.5" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={3} className="w-full text-[13px] border border-[#d0d5dd] rounded-lg px-2.5 py-1.5 resize-none" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className={btnSecondary}>Cancel</button>
          <button onClick={save} disabled={saving || !title.trim()} className={btnPrimary}>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────
export function DashboardsPage({ brandId, agents, onBack }: DashboardsPageProps) {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [listFilter, setListFilter] = useState<'all' | 'always-on'>('all');
  const [listSearch, setListSearch] = useState('');
  const [refreshing, setRefreshing] = useState<'data' | 'redesign' | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [openPresentationId, setOpenPresentationId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Dashboard | null>(null);
  const [alwaysOnFor, setAlwaysOnFor] = useState<Dashboard | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchDashboards = useCallback(async () => {
    try {
      const { dashboards: data } = await coworkApi.dashboards.list(brandId);
      setDashboards(data);
      return data;
    } catch (err) {
      console.error('[DashboardsPage] Failed to fetch dashboards:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetchDashboards(); }, [fetchDashboards]);
  // Deep link: /agents/dashboards?open=<id> (the "team only" share link) opens that dashboard.
  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get('open');
    if (!openId) return;
    coworkApi.dashboards.get(openId)
      .then((d) => { setSelectedDashboard(d); const u = new URL(window.location.href); u.searchParams.delete('open'); window.history.replaceState({}, '', u.toString()); })
      .catch((e: Error) => toast.error(`Could not open the dashboard: ${e.message}`));
  }, []);

  // Poll while anything is rendering (agent-created dashboards land in the
  // list as 'rendering' and flip to 'ready' 1–3 minutes later).
  const anyRendering = useMemo(
    () => dashboards.some(d => d.renderStatus === 'rendering') || selectedDashboard?.renderStatus === 'rendering',
    [dashboards, selectedDashboard],
  );
  useEffect(() => {
    if (!anyRendering) return;
    const t = setInterval(async () => {
      const list = await fetchDashboards();
      if (selectedDashboard && list) {
        const next = list.find(d => d.id === selectedDashboard.id);
        if (next && next.renderStatus !== 'rendering') {
          try { setSelectedDashboard(await coworkApi.dashboards.get(next.id)); } catch { /* keep old */ }
          if (next.renderStatus === 'ready') toast.success(`"${next.title}" is ready`);
          if (next.renderStatus === 'failed') toast.error(`Render failed: ${next.renderError || 'unknown error'}`);
        }
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [anyRendering, fetchDashboards, selectedDashboard]);

  const applyUpdate = (updated: Dashboard) => {
    setDashboards(prev => prev.map(d => (d.id === updated.id ? { ...d, ...updated, html: undefined } : d)));
    setSelectedDashboard(prev => (prev && prev.id === updated.id ? { ...prev, ...updated, html: prev.html } : prev));
  };

  const handleSelect = async (dashboard: Dashboard) => {
    try {
      const full = await coworkApi.dashboards.get(dashboard.id);
      setSelectedDashboard(full);
    } catch (err) {
      console.error('[DashboardsPage] Failed to fetch dashboard:', err);
    }
  };

  const handleRefresh = async (dash: Dashboard, redesign: boolean) => {
    const mode = redesign || !dash.templateReady ? 'redesign' : 'data';
    if (selectedDashboard?.id === dash.id) setRefreshing(mode);
    setBusyId(dash.id);
    try {
      const r = await coworkApi.dashboards.refresh(dash.id, { redesign });
      if (r.renderStatus === 'rendering') {
        toast.message(mode === 'redesign' ? 'Redesigning: usually 1-3 minutes' : 'Rendering…');
        const list = await fetchDashboards();
        const next = list?.find(d => d.id === dash.id);
        if (selectedDashboard?.id === dash.id && next) setSelectedDashboard({ ...selectedDashboard, ...next, html: selectedDashboard.html });
      } else {
        const failed = (r.datasets || []).filter(d => d.error);
        if (failed.length) toast.warning(`Refreshed, but ${failed.length} source${failed.length > 1 ? 's' : ''} failed: ${failed.map(f => f.label).join(', ')}`);
        else toast.success('Data refreshed');
        await fetchDashboards();
        if (selectedDashboard?.id === dash.id) setSelectedDashboard(await coworkApi.dashboards.get(dash.id));
      }
    } catch (err) {
      console.error('[DashboardsPage] Refresh failed:', err);
      toast.error(`Refresh failed: ${(err as Error).message || 'unknown error'}`);
      await fetchDashboards();
    } finally {
      setRefreshing(null);
      setBusyId(null);
    }
  };

  const handleDelete = async (dash: Dashboard) => {
    if (!confirm(`Delete "${dash.title}"? This cannot be undone.`)) return;
    setBusyId(dash.id);
    try {
      await coworkApi.dashboards.delete(dash.id);
      if (selectedDashboard?.id === dash.id) setSelectedDashboard(null);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(dash.id); return n; });
      await fetchDashboards();
    } catch (err) {
      console.error('[DashboardsPage] Delete failed:', err);
      toast.error('Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDuplicate = async (dash: Dashboard) => {
    setBusyId(dash.id);
    try {
      const copy = await coworkApi.dashboards.duplicate(dash.id);
      await fetchDashboards();
      toast.success(`Duplicated as "${copy.title}"`);
    } catch (err) {
      console.error('[DashboardsPage] Duplicate failed:', err);
      toast.error('Duplicate failed');
    } finally {
      setBusyId(null);
    }
  };

  const toggleAlwaysOnQuick = async (dash: Dashboard) => {
    if (!dash.autoRefresh) { setAlwaysOnFor(dash); return; }
    try {
      applyUpdate(await coworkApi.dashboards.update(dash.id, { autoRefresh: false }));
      toast.success('Always-on turned off');
    } catch (err) {
      console.error('[DashboardsPage] Toggle always-on failed:', err);
      toast.error('Could not update');
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = async (ids: string[]) => {
    setExporting(true);
    try {
      const { presentationId } = await coworkApi.dashboards.export(ids);
      setOpenPresentationId(presentationId);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('[DashboardsPage] Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getCreatorInfo = (createdBy: string) => {
    const agent = agents.find(a => a.id === createdBy);
    if (agent) return { name: agent.name, emoji: agent.avatarEmoji, color: agent.accentColor };
    return { name: 'You', emoji: '', color: '#667085' };
  };

  const dialogs = (
    <>
      {creating && <CreateDashboardDialog brandId={brandId} brandName={undefined} onClose={() => setCreating(false)} onCreated={async (id) => { setCreating(false); await fetchDashboards(); try { setSelectedDashboard(await coworkApi.dashboards.get(id)); } catch { /* list refreshed */ } }} />}
      {openPresentationId && (
        <PresentationModal presentationId={openPresentationId} onClose={() => setOpenPresentationId(null)} />
      )}
      {editing && <EditDialog dash={editing} onClose={() => setEditing(null)} onSaved={applyUpdate} />}
      {alwaysOnFor && <AlwaysOnDialog dash={alwaysOnFor} onClose={() => setAlwaysOnFor(null)} onSaved={applyUpdate} />}
    </>
  );

  const CardMenu = ({ dash }: { dash: Dashboard }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="p-1 rounded-md text-[#98a2b3] hover:bg-[#f2f4f7] hover:text-[#344054] transition-colors"
          aria-label="Dashboard actions"
        >
          {busyId === dash.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 text-[13px]" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => handleSelect(dash)}><ExternalLink className="w-3.5 h-3.5" /> Open</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setEditing(dash)}><Pencil className="w-3.5 h-3.5" /> Edit details</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleDuplicate(dash)}><Copy className="w-3.5 h-3.5" /> Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={dash.renderStatus === 'rendering'} onClick={() => handleRefresh(dash, false)}>
          <RefreshCw className="w-3.5 h-3.5" /> {dash.templateReady ? 'Refresh data' : 'Generate'}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={dash.renderStatus === 'rendering'} onClick={() => handleRefresh(dash, true)}>
          <Sparkles className="w-3.5 h-3.5" /> Redesign with AI
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setAlwaysOnFor(dash)}>
          <Zap className="w-3.5 h-3.5" /> {dash.autoRefresh ? 'Always-on settings…' : 'Make always-on…'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {!(dash as any).hasSpec && <DropdownMenuItem onClick={async () => { try { await coworkApi.dashboards.rebuild(dash.id); toast.success('Rebuilding in the new design…'); setTimeout(() => void fetchDashboards(), 4000); } catch (e) { toast.error((e as Error).message); } }}><Sparkles className="w-3.5 h-3.5" /> Rebuild in the new design</DropdownMenuItem>}
        <DropdownMenuItem variant="destructive" onClick={() => handleDelete(dash)}><Trash2 className="w-3.5 h-3.5" /> Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // ── Detail view ─────────────────────────────────────────────────
  if (selectedDashboard && (selectedDashboard as any).hasSpec) {
    // v2 (spec) dashboards render natively in the studio, with Kai's dock and configurable tables.
    return <DashboardStudio dashboardId={selectedDashboard.id} brandId={brandId} onBack={() => { setSelectedDashboard(null); void fetchDashboards(); }} onDeleted={() => void fetchDashboards()} />;
  }
  if (selectedDashboard) {
    const dash = selectedDashboard;
    const embedUrl = dash.shareToken ? coworkApi.dashboards.embedUrl(dash.id, dash.shareToken) : '';
    const rendering = dash.renderStatus === 'rendering';

    return (
      <>
      {dialogs}
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#eaecf0] gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSelectedDashboard(null)} className="p-1 rounded-md hover:bg-[#f9fafb] transition-colors shrink-0">
              <ArrowLeft className="w-4 h-4 text-[#667085]" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[15px] font-semibold text-[#101828] truncate">{dash.title}</h1>
                <RenderStatusChip dash={dash} size="md" />
                <AlwaysOnChip dash={dash} size="md" />
              </div>
              {dash.description && <p className="text-[12px] text-[#667085] truncate">{dash.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {dash.lastRefreshedAt && (
              <span className="text-[11px] text-[#98a2b3] flex items-center gap-1">
                <Clock className="w-3 h-3" /> Refreshed {formatTime(dash.lastRefreshedAt)}
              </span>
            )}
            <button
              onClick={() => handleRefresh(dash, false)}
              disabled={!!refreshing || rendering}
              title={dash.templateReady ? 'Re-run the data sources into the saved layout (seconds, no AI)' : 'Generate the dashboard with AI'}
              className={btnSecondary}
            >
              <RefreshCw className={cn('w-3.5 h-3.5', refreshing === 'data' && 'animate-spin')} />
              {dash.templateReady ? 'Refresh data' : 'Generate'}
            </button>
            <button
              onClick={() => handleRefresh(dash, true)}
              disabled={!!refreshing || rendering}
              title="Ask the AI for a new layout (1-3 minutes)"
              className={btnSecondary}
            >
              <Sparkles className={cn('w-3.5 h-3.5', refreshing === 'redesign' && 'animate-pulse')} /> Redesign
            </button>
            <button
              onClick={() => setAlwaysOnFor(dash)}
              className={cn(btnSecondary, dash.autoRefresh && 'border-[#a6f4c5] bg-[#ecfdf5] text-[#047857] hover:bg-[#d1fadf]')}
              title={dash.autoRefresh ? `Always-on: ${intervalLabel(dash.refreshIntervalHours)}` : 'Refresh on a schedule and deliver'}
            >
              <Zap className="w-3.5 h-3.5" /> Always-on
            </button>
            <button onClick={() => handleExport([dash.id])} disabled={exporting} className={btnSecondary}>
              <Download className={cn('w-3.5 h-3.5', exporting && 'animate-pulse')} />
              {exporting ? 'Building…' : 'Export as report'}
            </button>
            <ShareLinkControl kind="dashboard" id={dash.id} shareEnabled={!!dash.shareEnabled} shareToken={dash.shareToken} canEdit={dash.isOwner !== false} onChanged={(v) => setSelectedDashboard((cur) => cur && cur.id === dash.id ? { ...cur, ...v } : cur)} />
            <CardMenu dash={dash} />
          </div>
        </div>

        {/* Status banners */}
        {rendering && (
          <div className="flex items-center gap-2 px-6 py-2 bg-[#eef2ff] text-[#4338ca] text-[12px] border-b border-[#e0e7ff]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Rendering: the AI is designing the layout around your data. This usually takes 1-3 minutes; the page updates automatically.
          </div>
        )}
        {dash.renderStatus === 'failed' && (
          <div className="flex items-center justify-between gap-3 px-6 py-2 bg-[#fef3f2] text-[#b42318] text-[12px] border-b border-[#fecdca]">
            <span className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">Last render failed: {dash.renderError || 'unknown error'}{dash.hasHtml ? ': showing the previous version.' : ''}</span>
            </span>
            <button onClick={() => handleRefresh(dash, !dash.templateReady)} className="text-[12px] font-semibold underline shrink-0">Retry</button>
          </div>
        )}

        {/* Iframe */}
        <div className="flex-1 overflow-hidden">
          {embedUrl && dash.hasHtml !== false && (dash.html || dash.hasHtml) ? (
            <iframe
              key={dash.lastRefreshedAt || dash.updatedAt}
              src={embedUrl}
              className="w-full h-full border-0"
              style={{ minHeight: 'calc(100vh - 8rem)' }}
              title={dash.title}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-[#98a2b3] text-[13px] gap-2">
              {rendering ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Rendering your dashboard…</>
              ) : (
                <>No rendered content yet. <button onClick={() => handleRefresh(dash, true)} className="text-[#6366f1] font-medium underline">Generate it now</button></>
              )}
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  // ── List view — same shell as Reports: title + intro, action top-right, filter chips + search, card grid ──
  const alwaysOnCount = dashboards.filter((d) => d.autoRefresh).length;
  const needsRebuild = dashboards.some((d) => !(d as any).hasSpec);
  const visible = dashboards.filter((d) => (listFilter === 'always-on' ? d.autoRefresh : true) && (!listSearch.trim() || d.title.toLowerCase().includes(listSearch.toLowerCase()) || (d.description || '').toLowerCase().includes(listSearch.toLowerCase())));
  return (
    <>
    {dialogs}
    <div className="min-h-full p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>Dashboards</h1>
          <p className="mt-1 text-[13px] text-[#667085]">Live views of your data that refresh on their own. For a written analysis, ask Kai for a report: decks land under Reports.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {needsRebuild && (
            <button onClick={async () => { try { const r = await coworkApi.dashboards.rebuildAll(brandId); toast.success(`Rebuilding ${r.queued} dashboard${r.queued === 1 ? '' : 's'} in the new design: Kai lays each one out from its live sources`); setTimeout(() => void fetchDashboards(), 4000); } catch (e) { toast.error((e as Error).message); } }} className="flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] px-3 py-2 text-[13px] font-medium text-[#344054] hover:bg-[#f9fafb]" title="Rebuild the older HTML dashboards in the new design">
              <RefreshCw className="h-3.5 w-3.5" /> Rebuild older ones ({dashboards.filter((d) => !(d as any).hasSpec).length})
            </button>
          )}
          <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-lg bg-[#be43ff] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-[#a835e0]"><Plus className="h-4 w-4" /> Create a dashboard</button>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[{ id: 'all', label: 'All', n: dashboards.length }, { id: 'always-on', label: 'Always-on', n: alwaysOnCount }].map((c) => {
          if (c.id !== 'all' && c.n === 0) return null;
          const active = listFilter === c.id;
          return (
            <button key={c.id} onClick={() => setListFilter(c.id as 'all' | 'always-on')} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${active ? 'border-[#be43ff] bg-[#fcf7ff] text-[#7c3aed]' : 'border-[#eaecf0] text-[#344054] hover:border-[#d0d5dd]'}`}>
              {c.id === 'always-on' && <Zap className="h-3 w-3 text-[#047857]" />}{c.label} <span className={`text-[11px] ${active ? 'text-[#a855f7]' : 'text-[#98a2b3]'}`}>{c.n}</span>
            </button>
          );
        })}
        <div className="relative ml-auto w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
          <input value={listSearch} onChange={(e) => setListSearch(e.target.value)} placeholder="Search dashboards…" className="w-full rounded-lg border border-[#d0d5dd] py-1.5 pl-8 pr-2 text-[13px]" />
        </div>
      </div>

      {loading && <div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>}

      {!loading && dashboards.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <BarChart3 className="mb-3 h-10 w-10 text-[#d0d5dd]" />
          <p className="mb-1 text-[14px] text-[#667085]">No dashboards yet</p>
          <p className="max-w-sm text-[12px] text-[#98a2b3]">Create one from a template, or ask Kai to "build a live dashboard of our campaigns": dashboards refresh on their own; written analyses and slide decks live under Reports.</p>
        </div>
      )}
      {!loading && dashboards.length > 0 && visible.length === 0 && <div className="py-20 text-center text-[13px] text-[#98a2b3]">Nothing matches.</div>}

      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-xl bg-[#f0ecff] px-4 py-2.5">
          <span className="text-[13px] font-medium text-[#5b21b6]">{selectedIds.size} dashboard{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => handleExport(Array.from(selectedIds))} disabled={exporting} className={btnPrimary}>
              <Download className={cn('w-3.5 h-3.5', exporting && 'animate-pulse')} />
              {exporting ? 'Building…' : `Export as ${selectedIds.size > 1 ? 'combined ' : ''}report`}
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="px-2 text-[12px] text-[#667085] hover:text-[#101828]">Clear</button>
          </div>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {visible.map((dash) => {
            const creator = getCreatorInfo(dash.createdBy);
            const typeStyle = TYPE_STYLES[dash.type] || TYPE_STYLES.custom;
            const selected = selectedIds.has(dash.id);
            return (
              <div key={dash.id} className={cn('group relative overflow-hidden rounded-xl border bg-white transition hover:shadow-md', selected ? 'border-[#be43ff]' : dash.renderStatus === 'failed' ? 'border-[#fecdca] hover:border-[#fda29b]' : 'border-[#eaecf0] hover:border-[#d0d5dd]')}>
                <button type="button" onClick={() => handleSelect(dash)} className="block w-full text-left">
                  {/* Portrait preview — dashboards run long, so the card shows the top of the real thing. */}
                  <div style={{ position: 'relative', aspectRatio: '3 / 4', overflow: 'hidden', background: '#f8f9fc' }}>
                    <DashboardPreview dashboardId={dash.id} hasSpec={!!(dash as any).hasSpec} accent={typeStyle.text} />
                    {dash.renderStatus === 'rendering' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85 px-6 backdrop-blur-[1px]">
                        <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" />
                        <div className="text-[12px] font-semibold text-[#101828]">Kai is building…</div>
                      </div>
                    )}
                    {dash.renderStatus === 'failed' && (
                      <div className="absolute inset-x-0 bottom-0 bg-[#fef3f2] px-3 py-2 text-[11px] text-[#b42318]" title={dash.renderError || ''}>Build failed{dash.renderError ? `: ${dash.renderError}` : ''}</div>
                    )}
                  </div>
                  <div className="px-4 pb-3 pt-3">
                    <div className="line-clamp-2 text-[14px] font-semibold text-[#101828]">{dash.title}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-[#667085]">
                      <span className="rounded-full px-1.5 py-0.5 font-medium" style={{ backgroundColor: typeStyle.bg, color: typeStyle.text }}>{typeStyle.label}</span>
                      {dash.autoRefresh && <span className="flex items-center gap-1 text-[#047857]" title="Refreshes on its own"><Zap className="h-3 w-3" /> always-on</span>}
                      {dash.visibility === 'private' && <span title="Only you can see this" className="flex items-center gap-1"><Lock className="h-3 w-3" /> only me</span>}
                      {dash.visibility === 'members' && <span title="Shared with picked team members" className="flex items-center gap-1"><Users className="h-3 w-3" /> {dash.allowedUserIds?.length || 0}</span>}
                      {dash.status === 'archived' && <span className="rounded-full bg-[#f2f4f7] px-1.5 py-0.5">Archived</span>}
                      <span className="ml-auto flex items-center gap-1" title={`${creator.name}${dash.lastRefreshedAt ? ` · data ${formatTime(dash.lastRefreshedAt)}` : ''}`}>{creator.emoji} {dash.lastRefreshedAt ? formatTime(dash.lastRefreshedAt) : creator.name}</span>
                    </div>
                  </div>
                </button>
                <div className={cn('absolute left-2 top-2 z-10 transition', selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
                  <button onClick={(e) => toggleSelect(dash.id, e)} className="rounded-md bg-white/95 p-1 text-[#98a2b3] shadow hover:text-[#be43ff]" title="Select for a combined report">
                    {selected ? <CheckSquare className="h-4 w-4 text-[#be43ff]" /> : <Square className="h-4 w-4" />}
                  </button>
                </div>
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                  <button onClick={(e) => { e.stopPropagation(); toggleAlwaysOnQuick(dash); }} title={dash.autoRefresh ? 'Turn always-on off' : 'Make always-on'} className={cn('rounded-md bg-white/95 p-1.5 shadow', dash.autoRefresh ? 'text-[#047857]' : 'text-[#667085] hover:text-[#047857]')}><Zap className="h-3.5 w-3.5" /></button>
                  <div className="rounded-md bg-white/95 shadow"><CardMenu dash={dash} /></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}
